import { z } from "zod";

export const WorkItemSchema = z.object({
    id: z.string(),
    workflowId: z.string(),
    // Entity coordinates duplicated onto each work item so queue-style
    // worklists can deep-link into the parent workflow without a
    // follow-up fetch to resolve the partition key. Defaulted to empty
    // strings for backwards compatibility with older payloads written
    // before the BFF started surfacing them — the consuming UI should
    // treat the empty case as "navigation disabled" rather than crash.
    entityType: z.string().default(""),
    entityId: z.string().default(""),
    type: z.string(),
    // Closed task-type classification carried only by task work items
    // (e.g. "send-rate-confirmation"); null for messages, notes, and
    // legacy tasks written before the field existed. Drives the rail's
    // task label. Mirrors WorkItemResponse.TaskType on the wire.
    taskType: z.string().nullable().optional().default(null),
    // Free-text content. Optional/nullable on the wire because tasks may
    // be created without a body (their taskType carries the actionable
    // meaning). Coerced to "" at the boundary so every render site can keep
    // treating body as a guaranteed string; messages and notes always
    // carry one.
    body: z.string().nullish().transform((value) => value ?? ""),
    createdAt: z.string(),
    createdBy: z.string().nullable(),
    // Display-name snapshots paired with the durable Entra OIDs in
    // createdBy/completedBy. Renderers should prefer these and fall back
    // to the id only when no snapshot was captured (which is true for
    // documents written before this field existed).
    createdByDisplayName: z.string().nullable().optional().default(null),
    dueDate: z.string().nullable(),
    completedAt: z.string().nullable(),
    completedBy: z.string().nullable(),
    completedByDisplayName: z.string().nullable().optional().default(null),
    // Approval snapshot: set when a member of requestedByQueueId has
    // accepted the completed work item. Null while pending review or
    // after a rejection has reopened the item.
    approvedAt: z.string().nullable().optional().default(null),
    approvedBy: z.string().nullable().optional().default(null),
    approvedByDisplayName: z.string().nullable().optional().default(null),
    // Originating queue captured when this item was spawned from another
    // queue via the @-mention composer. Drives the approval surface and
    // is rendered as a chip ("Requested by Customer Success") in the
    // child-thread header. Null for normal items.
    requestedByQueueId: z.string().nullable().optional().default(null),
    requestedByQueueName: z.string().nullable().optional().default(null),
    // Dispenser lock projection (docs/NEXT.md). lockedBy is the holder's
    // Entra OID, lockedByDisplayName the render snapshot, lockedUntil the
    // expiry. All null when the item is free, and on payloads written
    // before the lock primitive existed. The manual-navigation banner
    // treats the item as free once the client clock passes lockedUntil,
    // mirroring the server-side read-time guard, so an expired-but-
    // uncleared lock never renders as held.
    lockedBy: z.string().nullable().optional().default(null),
    lockedByDisplayName: z.string().nullable().optional().default(null),
    lockedUntil: z.string().nullable().optional().default(null),
    // Projection-only field: number of child workflows spawned off
    // this work item. Computed server-side per read so the badge
    // doesn't require a per-card fetch. Defaults to 0 for compatibility
    // with older payloads that predate the spawn flow.
    childWorkflowCount: z.number().int().nonnegative().optional().default(0),
    // Whether this note is pinned to the top of the task/note rail.
    // Always false for tasks and messages (note-only affordance);
    // persisted and shared across viewers. Defaults false for payloads
    // written before the field existed.
    pinned: z.boolean().optional().default(false),
    // Optional link from a note to the work item it annotates. Null for a
    // workflow-root note (the historical shape) and every non-note type.
    // A note renders under the work item whose id this matches; defaults
    // null for payloads written before the field existed.
    parentWorkItemId: z.string().nullable().optional().default(null),
    assignedGroups: z.array(z.string()),
    // Two queue lists, one purpose each:
    //   - visibleToQueueIds: the conversational audience, i.e. queues
    //     that should *see* this item in their listing/timeline. The
    //     asker's own queue lives here for spawn/Ask items so the asker
    //     keeps a chat-style record of their outgoing question.
    //   - awaitingActionByQueueIds: the workload accounting set, i.e.
    //     queues whose tally badge should tick up because they're owed
    //     an action on this item. The asker is *excluded* on outgoing
    //     spawn/Ask items (a question you ask isn't on your to-do list)
    //     and re-included only after the recipient completes the work
    //     and an approval beat is owed back to them.
    //
    // Visibility lists are ergonomic for cards and threads; awaiting
    // lists drive every queue-badge tally. UI code that wants "items
    // this queue can see" reads visibleToQueueIds; code that wants
    // "items this queue owes work on" reads awaitingActionByQueueIds.
    visibleToQueueIds: z.array(z.string()).default([]),
    awaitingActionByQueueIds: z.array(z.string()).default([]),
    routingTags: z.record(z.string(), z.string()).default({}),
    // Optional producer-supplied absolute https URL the "Go" affordance opens
    // for this item, taking precedence over the computed deep link. Null when
    // the item relies on deep-link resolution and on payloads written before
    // the field existed. Einstein guarantees only structural safety (absolute,
    // https, bounded) at the boundary — the client still decides in-app relay
    // vs. new tab by matching the URL's origin against the Galileo origin.
    externalActionUrl: z.string().nullable().optional().default(null),
});

export type WorkItem = z.infer<typeof WorkItemSchema>;

// Per-workflow rollup of work items bucketed by lifecycle state. Returned
// only by surfaces that can compute it cheaply (the entity-pivoted
// listing, which already shares a Cosmos partition with the items).
// Other endpoints leave this null; consumers should branch on presence.
export const WorkItemTallySchema = z.object({
    completed: z.number().int().nonnegative(),
    open: z.number().int().nonnegative(),
    pending: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
});

export type WorkItemTally = z.infer<typeof WorkItemTallySchema>;

export const WorkflowSummarySchema = z.object({
    id: z.string(),
    entityType: z.string(),
    entityId: z.string(),
    entityDisplayName: z.string(),
    kind: z.string(),
    title: z.string(),
    createdAt: z.string(),
    createdBy: z.string().nullable(),
    createdByDisplayName: z.string().nullable().optional().default(null),
    parentWorkflowId: z.string().nullable(),
    parentWorkItemId: z.string().nullable(),
    // Optional projection: present on entity-listing responses, absent on
    // recent / search / child-listing responses. The default-to-null
    // pattern matches the rest of this schema and keeps clients written
    // against pre-tally servers parsing cleanly.
    workItemTally: WorkItemTallySchema.nullable().optional().default(null),
});

export type WorkflowSummary = z.infer<typeof WorkflowSummarySchema>;

export const WorkflowSchema = WorkflowSummarySchema.extend({
    workItems: z.array(WorkItemSchema),
});

export type Workflow = z.infer<typeof WorkflowSchema>;

export const WorkflowListResponseSchema = z.object({
    items: z.array(WorkflowSummarySchema),
    totalCount: z.number(),
});

export type WorkflowListResponse = z.infer<typeof WorkflowListResponseSchema>;

// Mirrors WorkflowKindsResponse on the wire. The list is the registry from
// Galileo.Einstein.Contracts.Workflows.WorkflowKinds; the frontend renders
// it as-is so the two stay in lockstep without a TypeScript enum to bump.
export const WorkflowKindsResponseSchema = z.object({
    kinds: z.array(z.string()),
});

export type WorkflowKindsResponse = z.infer<typeof WorkflowKindsResponseSchema>;

// Authoritative task-type vocabulary published by the contracts-backed API.
// Keep the wire values unchanged; presentation formatting belongs at render sites.
export const TaskTypesResponseSchema = z.object({
    taskTypes: z.array(z.string()),
});

export type TaskTypesResponse = z.infer<typeof TaskTypesResponseSchema>;

// Returned by POST /workflows/{id}/close-conversation. The id list lets
// the frontend reconcile its cache surgically (mark exactly these items
// as completed) rather than refetching the workflow on every close.
export const CloseConversationResponseSchema = z.object({
    closedWorkItemIds: z.array(z.string()),
});

export type CloseConversationResponse = z.infer<typeof CloseConversationResponseSchema>;
